import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

type PublicTripView = {
  id: string;
  name: string;
  description: string | null;
  cover_image_url: string | null;
  start_date: string;
  end_date: string;
  requires_approval: boolean | null;
};

export default function JoinTripPage() {
  const { joinCode } = useParams();
  const navigate = useNavigate();

  const [trip, setTrip] = useState<PublicTripView | null>(null);
  const [loading, setLoading] = useState(true);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [requestPending, setRequestPending] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  useEffect(() => {
    if (!joinCode) return;

    const fetchTrip = async () => {
      const { data, error } = await supabase
        .from("trips")
        .select(
          "id, name, description, cover_image_url, start_date, end_date, requires_approval"
        )
        .eq("join_code", joinCode)
        .single();

      if (error) {
        console.error("Fehler beim Laden der Reise:", error);
        setError("Reise nicht gefunden oder nicht zugänglich.");
        setTrip(null);
      } else {
        setTrip(data);
      }

      setLoading(false);
    };

    fetchTrip();
  }, [joinCode]);

  const handleJoin = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      alert("Bitte logge dich ein oder registriere dich zuerst.");
      navigate(`/?redirect=/join/${joinCode}`);
      return;
    }

    if (!trip) return;

    // 0) Schon Teilnehmer?
    const { data: existingParticipant, error: participantError } = await supabase
      .from("trip_participants")
      .select("*")
      .eq("trip_id", trip.id)
      .eq("user_id", user.id)
      .maybeSingle();

    if (participantError) {
      console.error(
        "Fehler beim Prüfen der Teilnehmerliste:",
        participantError.message
      );
      setError("Fehler beim Prüfen deiner Teilnahme.");
      return;
    }

    if (existingParticipant) {
      if ((existingParticipant as any).is_blocked) {
        setError("❌ Du wurdest von dieser Reise ausgeschlossen.");
      } else {
        setJoined(true);
      }
      return;
    }

    // 1) Wenn Genehmigungspflicht aktiv, erst Request-Flow
    if (trip.requires_approval) {
      const { data: existingRequest, error: requestError } = await supabase
        .from("trip_participant_requests")
        .select("*")
        .eq("trip_id", trip.id)
        .eq("user_id", user.id)
        .in("status", ["pending", "approved"])
        .maybeSingle();

      if (requestError) {
        console.error(
          "Fehler beim Prüfen bestehender Anfrage:",
          requestError.message
        );
        setError("Teilnahmeanfrage konnte nicht geprüft werden.");
        return;
      }

      // a) Bereits genehmigt → jetzt *über RPC* beitreten
      if (existingRequest?.status === "approved") {
        const { data: tripId2, error: joinErr } = await supabase.rpc(
          "accept_join_code",
          { p_join_code: joinCode! }
        );
        if (joinErr) {
          console.error("Fehler beim Beitreten (RPC):", joinErr.message);
          setError(joinErr.message || "Fehler beim Beitreten zur Reise.");
        } else {
          setJoined(true);
          if (tripId2) navigate(`/trip/${tripId2}`);
        }
        return;
      }

      // b) Bereits angefragt → Hinweis
      if (existingRequest?.status === "pending") {
        setRequestPending(true);
        return;
      }

      // c) Noch keine Anfrage → anlegen
      const { error: insertRequestError } = await supabase
        .from("trip_participant_requests")
        .insert([{ trip_id: trip.id, user_id: user.id, status: "pending" }]);

      if (insertRequestError) {
        console.error(
          "Fehler beim Anlegen der Anfrage:",
          insertRequestError.message
        );
        setError("Fehler beim Einreichen der Anfrage.");
      } else {
        setRequestSubmitted(true);
      }
      return;
    }

    // 2) Keine Genehmigung nötig → direkt *über RPC* beitreten
    const { data: tripId, error: rpcError } = await supabase.rpc(
      "accept_join_code",
      { p_join_code: joinCode! }
    );

    if (rpcError) {
      console.error("Fehler beim Beitreten (RPC):", rpcError.message);
      if (/Ungültig|abgelaufen/i.test(rpcError.message)) {
        setError("Dieser Einladungslink ist ungültig oder abgelaufen.");
      } else {
        setError("Fehler beim Beitreten zur Reise.");
      }
      return;
    }

    setJoined(true);
    if (tripId) navigate(`/trip/${tripId}`);
  };

  if (loading) return <p>Lade Reise…</p>;
  if (error || !trip)
    return <p>{error ?? "Reise konnte nicht geladen werden."}</p>;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <img
        src={trip.cover_image_url ?? undefined}
        alt={trip.name}
        className="w-full h-64 object-cover rounded-xl mb-4 shadow"
      />
      <h1 className="text-2xl font-bold mb-2">{trip.name}</h1>
      <p className="text-gray-600 mb-4">{trip.description}</p>
      <p className="mb-4 text-sm text-gray-500">
        {new Date(trip.start_date).toLocaleDateString()} –{" "}
        {new Date(trip.end_date).toLocaleDateString()}
      </p>

      {requestPending ? (
        <p className="text-yellow-600 font-semibold mt-6 text-center">
          🕓 Deine Anfrage zur Teilnahme wurde bereits gestellt und ist noch
          offen.
        </p>
      ) : requestSubmitted ? (
        <p className="text-green-600 font-semibold mt-6 text-center">
          ✅ Deine Anfrage wurde eingereicht! Das Orga-Team wird dich bald
          freischalten.
        </p>
      ) : joined ? (
        <div className="text-center mt-6">
          <p className="text-green-600 font-semibold mb-4">
            🎉 Du bist bereits Teilnehmer dieser Reise!
          </p>
          <button
            onClick={() => navigate(`/trip/${trip.id}`)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded shadow"
          >
            Zur Reiseansicht
          </button>
        </div>
      ) : (
        <button
          onClick={handleJoin}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow"
        >
          Jetzt teilnehmen
        </button>
      )}
      <div className="text-center mt-8">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-blue-600 hover:underline text-sm"
        >
          ⬅️ Zurück zum Dashboard
        </button>
      </div>
    </div>
  );
}
